// Cake Merge (cake-sort) - DOM rendering, drag/tap interaction, GameDef export.

import { el, clear } from "../../lib/dom";
import { sfx, haptic } from "../../lib/feedback";
import type { GameDef, LevelView, LevelViewOptions } from "../types";
import { CAKE_LEVELS } from "./levels";
import {
  CAKE_SIZE,
  type CakeLevel,
  type Cell,
  type Plate,
  emptyBoard,
  emptyCount,
  isFull,
  makeRng,
  placePlate,
  spawnPlate,
} from "./engine";
import "./cakemerge.css";

const TRAY_SIZE = 3;
// Top (frosting) and side (sponge) tint per cake color, in engine color order.
const CAKE_COLORS: { top: string; side: string }[] = [
  { top: "#74bcff", side: "#3e86d6" }, // blauw
  { top: "#ff8fc4", side: "#d6538f" }, // roze
  { top: "#7cd98c", side: "#3fa85c" }, // groen
  { top: "#ffa45e", side: "#d97430" }, // oranje
  { top: "#ffd95e", side: "#d9a616" }, // geel
  { top: "#fff4e0", side: "#dbbd8f" }, // room
];

function createLevelView(opts: LevelViewOptions): LevelView {
  const level: CakeLevel = CAKE_LEVELS[(opts.level - 1) % CAKE_LEVELS.length];
  const baseSeed = 1000 + opts.level * 97;

  let cells: Cell[] = emptyBoard(level);
  let cakes = 0;
  let boosters = level.boosters;
  let tray: Plate[] = [];
  let selected = -1;
  let hammer = false;
  let finished = false;
  let restarts = 0;
  let rng = makeRng(baseSeed);

  const root = opts.mount;
  clear(root);
  root.classList.add("cm");

  const bar = el("div.cm-bar-fill");
  const progress = el("div.cm-progress", {}, bar, el("span.cm-progress-label"));
  const boardEl = el("div.cm-board");
  const stage = el("div.cm-stage", {}, boardEl);
  const trayEl = el("div.cm-tray");
  const hammerBtn = el("button.cm-booster", { onclick: toggleHammer, "aria-label": "Hamer booster" }, `🔨 ${boosters}`);
  const controls = el("div.cm-controls", {}, hammerBtn, el("button.cm-booster", { onclick: doRestart, "aria-label": "Opnieuw" }, "↺"));

  root.append(progress, stage, trayEl, controls);

  // Keep the board's cols:rows aspect ratio so cells stay square in whatever
  // space is left, instead of stretching to fill the play area (which distorted
  // taller boards on smaller screens). We size the board to a contain-fit of the
  // stage; the 1fr grid tracks then land exactly square.
  function fitBoard(): void {
    const availW = Math.min(stage.clientWidth, 460);
    const availH = stage.clientHeight;
    if (availW <= 0 || availH <= 0) return;
    const cs = getComputedStyle(boardEl);
    const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const gapX = parseFloat(cs.columnGap) || 0;
    const gapY = parseFloat(cs.rowGap) || 0;
    const cell = Math.floor(
      Math.max(
        0,
        Math.min(
          (availW - padX - gapX * (level.cols - 1)) / level.cols,
          (availH - padY - gapY * (level.rows - 1)) / level.rows,
        ),
      ),
    );
    boardEl.style.width = `${cell * level.cols + gapX * (level.cols - 1) + padX}px`;
    boardEl.style.height = `${cell * level.rows + gapY * (level.rows - 1) + padY}px`;
  }
  const boardObserver = new ResizeObserver(() => fitBoard());
  boardObserver.observe(stage);
  window.addEventListener("resize", fitBoard);

  // ---- Cake plate rendering (SVG: bord + 3D-taartpunten met kersjes) ----
  // Perspectief-illusie: elke taartpunt is een elliptische sector; een donkere
  // kopie iets lager erachter vormt de zichtbare zijkant (diepte).
  const r2 = (n: number): number => Math.round(n * 100) / 100;

  function plateSvg(plate: Plate): string {
    const cx = 50;
    const topCy = 33; // middelpunt bovenvlak taart
    const rx = 35;
    const ry = 19;
    const depth = 9; // hoogte van de taartzijkant
    const wedge = 360 / CAKE_SIZE;
    // hoek in graden vanaf 12 uur, met de klok mee (zoals de oude conic-gradient)
    const px = (a: number): number => r2(cx + rx * Math.sin((a * Math.PI) / 180));
    const py = (a: number, cy: number): number => r2(cy - ry * Math.cos((a * Math.PI) / 180));
    const sector = (a0: number, a1: number, cy: number): string =>
      `M ${cx} ${cy} L ${px(a0)} ${py(a0, cy)} A ${rx} ${ry} 0 0 1 ${px(a1)} ${py(a1, cy)} Z`;

    const sides: string[] = [];
    const tops: string[] = [];
    const dots: string[] = [];
    let a = 0;
    for (let c = 0; c < plate.length; c++) {
      for (let k = 0; k < plate[c]; k++) {
        const col = CAKE_COLORS[c];
        sides.push(`<path d="${sector(a, a + wedge, topCy + depth)}" fill="${col.side}"/>`);
        tops.push(
          `<path d="${sector(a, a + wedge, topCy)}" fill="${col.top}" stroke="#fff7ea" stroke-width="1.5" stroke-linejoin="round"/>`,
        );
        const mid = ((a + wedge / 2) * Math.PI) / 180;
        const dx = r2(cx + 0.6 * rx * Math.sin(mid));
        const dy = r2(topCy - 0.6 * ry * Math.cos(mid));
        dots.push(
          `<circle cx="${dx}" cy="${dy}" r="3.8" fill="#ff4d5e" stroke="rgba(140,20,35,0.4)" stroke-width="0.8"/>` +
            `<circle cx="${r2(dx - 1.2)}" cy="${r2(dy - 1.3)}" r="1.2" fill="#ffc6cc"/>`,
        );
        a += wedge;
      }
    }

    return (
      `<svg viewBox="0 0 100 78" xmlns="http://www.w3.org/2000/svg">` +
      // bordje: schaduw, wit vlak, sierrand
      `<ellipse cx="50" cy="50" rx="46" ry="25" fill="rgba(43,22,80,0.20)"/>` +
      `<ellipse cx="50" cy="48" rx="46" ry="25" fill="#fffdf6" stroke="#e0d2bf" stroke-width="1.5"/>` +
      `<ellipse cx="50" cy="48" rx="37" ry="18.5" fill="none" stroke="#efe3cf" stroke-width="1.5"/>` +
      sides.join("") +
      tops.join("") +
      dots.join("") +
      `</svg>`
    );
  }

  function makePlateEl(plate: Plate, small = false): HTMLElement {
    const node = el(`div.cm-plate${small ? ".small" : ""}`);
    node.innerHTML = plateSvg(plate);
    return node;
  }

  // ---- Tray ----
  function fillTray(): void {
    while (tray.length < TRAY_SIZE) tray.push(spawnPlate(level, rng));
  }
  function renderTray(): void {
    clear(trayEl);
    tray.forEach((plate, slot) => {
      const slotEl = el(`div.cm-tray-slot${slot === selected ? ".selected" : ""}`, { "data-slot": slot });
      slotEl.append(makePlateEl(plate, true));
      slotEl.addEventListener("pointerdown", (e) => onTrayDown(e, slot));
      trayEl.append(slotEl);
    });
  }

  // ---- Board ----
  function renderBoard(pops: Set<number> = new Set()): void {
    boardEl.style.setProperty("--cols", String(level.cols));
    boardEl.style.setProperty("--rows", String(level.rows));
    clear(boardEl);
    for (let i = 0; i < cells.length; i++) {
      const cellEl = el("div.cm-cell", { "data-cell": i });
      const plate = cells[i];
      if (plate) {
        const p = makePlateEl(plate);
        if (pops.has(i)) p.classList.add("pop");
        cellEl.append(p);
      }
      cellEl.addEventListener("pointerdown", (e) => onCellDown(e, i));
      boardEl.append(cellEl);
    }
  }

  // ---- Placement ----
  function place(cell: number, plate: Plate, slot: number): boolean {
    if (finished || cells[cell] != null) return false;
    const res = placePlate(level, cells, cell, plate);
    if (!res.placed) return false;
    cells = res.cells;
    cakes += res.cakesMade;
    if (res.cakesMade > 0) {
      sfx.clear();
      haptic(res.cakesMade > 1 ? [12, 24, 12] : 12);
    } else {
      sfx.place();
      haptic(6);
    }
    tray[slot] = spawnPlate(level, rng);
    selected = -1;
    renderBoard(res.changedCells);
    renderTray();
    updateProgress();
    if (cakes >= level.target) return win(), true;
    if (isFull(cells) && boosters <= 0) lose();
    return true;
  }

  // ---- Interaction (drag + tap) ----
  let dragGhost: HTMLElement | null = null;
  let dragSlot = -1;
  let dragPlate: Plate | null = null;
  let dragging = false;
  let downX = 0;
  let downY = 0;

  function onTrayDown(e: PointerEvent, slot: number): void {
    if (finished) return;
    if (dragSlot >= 0) return; // ignore a second finger while a drag is in progress
    e.preventDefault();
    if (hammer) {
      // Selecting a tray piece exits hammer mode to avoid conflicting state.
      hammer = false;
      hammerBtn.classList.remove("active");
    }
    dragSlot = slot;
    dragPlate = tray[slot];
    dragging = false;
    downX = e.clientX;
    downY = e.clientY;
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragUp);
    window.addEventListener("pointercancel", onDragUp);
  }

  function onDragMove(e: PointerEvent): void {
    if (dragSlot < 0 || !dragPlate) return;
    if (!dragging) {
      if (Math.hypot(e.clientX - downX, e.clientY - downY) < 8) return;
      dragging = true;
      dragGhost = makePlateEl(dragPlate);
      dragGhost.classList.add("cm-ghost");
      document.body.append(dragGhost);
    }
    if (dragGhost) {
      dragGhost.style.left = `${e.clientX}px`;
      dragGhost.style.top = `${e.clientY}px`;
    }
    highlightCellUnder(e.clientX, e.clientY);
  }

  function onDragUp(e: PointerEvent): void {
    window.removeEventListener("pointermove", onDragMove);
    window.removeEventListener("pointerup", onDragUp);
    window.removeEventListener("pointercancel", onDragUp);
    clearHighlight();
    dragGhost?.remove();
    dragGhost = null;
    const plate = dragPlate;
    const slot = dragSlot;
    dragSlot = -1;
    dragPlate = null;

    if (dragging) {
      const cell = cellUnder(e.clientX, e.clientY);
      if (cell >= 0 && cells[cell] == null && plate) place(cell, plate, slot);
      else sfx.error();
    } else {
      selected = selected === slot ? -1 : slot;
      renderTray();
      sfx.tap();
    }
    dragging = false;
  }

  function onCellDown(e: PointerEvent, cell: number): void {
    if (finished) return;
    if (hammer) {
      if (cells[cell] != null && boosters > 0) {
        e.preventDefault();
        cells = cells.slice();
        cells[cell] = null;
        boosters--;
        hammer = false;
        hammerBtn.classList.remove("active");
        hammerBtn.textContent = `🔨 ${boosters}`;
        sfx.clear();
        haptic(12);
        renderBoard();
      }
      return;
    }
    if (selected >= 0 && cells[cell] == null) {
      e.preventDefault();
      place(cell, tray[selected], selected);
    }
  }

  function cellUnder(x: number, y: number): number {
    const target = document.elementFromPoint(x, y);
    const cellEl = target?.closest<HTMLElement>(".cm-cell");
    if (!cellEl || !boardEl.contains(cellEl)) return -1;
    return Number(cellEl.dataset.cell);
  }

  let highlighted: HTMLElement | null = null;
  function highlightCellUnder(x: number, y: number): void {
    const cell = cellUnder(x, y);
    clearHighlight();
    if (cell >= 0 && cells[cell] == null) {
      const node = boardEl.children[cell] as HTMLElement;
      node.classList.add("drop-ok");
      highlighted = node;
    }
  }
  function clearHighlight(): void {
    highlighted?.classList.remove("drop-ok");
    highlighted = null;
  }

  // ---- Boosters ----
  function toggleHammer(): void {
    if (finished || boosters <= 0) return;
    hammer = !hammer;
    hammerBtn.classList.toggle("active", hammer);
    selected = -1;
    renderTray();
    sfx.tap();
  }

  // ---- Win / lose ----
  let overlay: HTMLElement | null = null;

  function starsFor(): number {
    const ratio = emptyCount(cells) / cells.length;
    if (ratio >= 0.5) return 3;
    if (ratio >= 0.3) return 2;
    return 1;
  }

  let winTimer: ReturnType<typeof setTimeout> | null = null;

  function win(): void {
    if (finished) return;
    finished = true;
    sfx.win();
    haptic([20, 40, 20]);
    winTimer = setTimeout(() => opts.onWin({ stars: starsFor(), score: cakes }), 350);
  }

  function lose(): void {
    if (finished) return;
    finished = true;
    sfx.lose();
    opts.onLose?.("Bord vol");
    overlay = el(
      "div.cm-overlay",
      {},
      el(
        "div.cm-overlay-card",
        {},
        el("h3", {}, "Bord vol!"),
        el("p", {}, `Taarten: ${cakes} / ${level.target}`),
        el("button.cm-booster.primary", { onclick: doRestart }, "↺ Opnieuw"),
        el("button.cm-booster", { onclick: opts.requestExit }, "Levels"),
      ),
    );
    root.append(overlay);
  }

  function doRestart(): void {
    restarts++;
    cells = emptyBoard(level);
    cakes = 0;
    boosters = level.boosters;
    tray = [];
    selected = -1;
    hammer = false;
    finished = false;
    rng = makeRng(baseSeed + restarts * 1234);
    overlay?.remove();
    overlay = null;
    hammerBtn.classList.remove("active");
    hammerBtn.textContent = `🔨 ${boosters}`;
    fillTray();
    renderBoard();
    renderTray();
    updateProgress();
  }

  function updateProgress(): void {
    const pct = Math.min(100, (cakes / level.target) * 100);
    bar.style.width = `${pct}%`;
    const label = progress.querySelector(".cm-progress-label");
    if (label) label.textContent = `${cakes} / ${level.target} taarten`;
    opts.setStatus?.(`🎂 ${cakes}/${level.target}`);
  }

  // ---- Init ----
  fillTray();
  renderBoard();
  renderTray();
  updateProgress();
  // The shell mounts this screen right AFTER createLevelView returns, so the
  // board has no size yet here. Measure on the next task (a timeout fires
  // reliably even when rAF/ResizeObserver are throttled by a non-painting tab).
  const fitTimer = setTimeout(fitBoard, 0);

  return {
    destroy() {
      if (winTimer) clearTimeout(winTimer);
      clearTimeout(fitTimer);
      boardObserver.disconnect();
      window.removeEventListener("resize", fitBoard);
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onDragUp);
      window.removeEventListener("pointercancel", onDragUp);
      dragGhost?.remove();
    },
    restart: doRestart,
  };
}

export const cakeMerge: GameDef = {
  id: "cakemerge",
  title: "Cake Merge",
  tagline: "Sorteer de taartpunten op kleur",
  accent: "#0891b2",
  icon: "🎂",
  levelCount: CAKE_LEVELS.length,
  createLevelView,
};
